/**
 * One trace id per turn — `docs/logging-and-observability.md` §3.
 *
 * The renderer mints it, sends it as `X-Trace-Id` on `/ask`, and the agent
 * carries it through `AsyncLocalStorage` into every line it logs for that turn
 * and on to the plugin. When a turn fails, the same id is shown on the error
 * card as `Reference: …`, so what the user reads to support is the exact key
 * that finds the turn in the logs.
 *
 * The shape is W3C `trace-id`: 32 lowercase hex characters, no dashes. Not a
 * UUID — the agent's own `_tracing` mints the same shape when no header comes,
 * and one grep pattern should match both.
 */

/** 16 random bytes as 32 lowercase hex characters. */
export function newTraceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/** Whether a value is a well-formed trace id (what `newTraceId` mints). */
export function isTraceId(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{32}$/.test(value);
}
